import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { LocalHospital as MedicalIcon } from '@mui/icons-material';

/**
 * Wartezeit-Chips je Fahrzeugtyp plus Verfügbarkeit der Spezialfahrzeuge.
 * Gemeinsam für Dashboard und Bestellformular, damit Farben, Kurzform und
 * Nachtmodus-Lesbarkeit an einer Stelle stehen.
 *
 * `compact` (Handy): eine wischbare Zeile statt umbrechender Chips, Kurzform
 * „5–10′" (Symbol nennt den Fahrzeugtyp), Spezialfahrzeuge nur als Symbol in
 * Ampelfarbe. Der volle Text steht jeweils im Tooltip.
 */

export const getWaitTimeSeverity = (waitTime) => {
  const value = String(waitTime || '');
  if (value.includes('nicht verfügbar') ||
      value.includes('Keine Fahrer verfügbar') ||
      value.includes('30+') ||
      value.includes('Fehler')) return 'error';
  if (value.includes('nach Ausfallzeit') ||
      value.startsWith('2') ||
      value.startsWith('3')) return 'warning';
  return 'success';
};

// Kurzform für die Wartezeit-Chips auf dem Handy: Das Symbol nennt den
// Fahrzeugtyp, übrig bleibt die Zeit („5–10′", „30+′"). „–" = kein Fahrer,
// „*" = Schätzung nach Ausfallzeit. Der volle Text steht im Tooltip.
export const shortWaitTime = (waitTime, pendingLabel = '–') => {
  const value = String(waitTime || '');
  if (!value) return pendingLabel;
  if (value.includes('Keine Fahrer') || value.includes('nicht verfügbar')) return '–';
  if (value.includes('Fehler')) return '!';
  const match = value.match(/\d+\s*[-–]\s*\d+|\d+\+?/);
  if (!match) return value;
  const time = match[0].replace(/\s*[-–]\s*/, '–');
  return `${time}′${value.includes('nach Ausfallzeit') ? '*' : ''}`;
};

const SOFT_BG = {
  error: 'rgba(211, 47, 47, 0.08)',
  warning: 'rgba(237, 108, 2, 0.08)',
  success: 'rgba(46, 125, 50, 0.08)',
};

// Nachtmodus: .dark ginge auf dunklem Grund unter
const readableSeverityColor = (severity) => (theme) =>
  theme.palette[severity][theme.palette.mode === 'dark' ? 'light' : 'dark'];

const chipSx = (severity, compact, height) => ({
  height,
  borderColor: `${severity}.main`,
  color: readableSeverityColor(severity),
  bgcolor: SOFT_BG[severity],
  fontWeight: 700,
  '& .MuiChip-icon': { color: 'inherit' },
  ...(compact && {
    '& .MuiChip-label': { px: 0.5 },
    '& .MuiChip-icon': { color: 'inherit', ml: 0.5, mr: 0 },
  }),
});

/**
 * @param waitTimeItems  [{ key, label, shortLabel?, value, icon }]
 * @param specialCapabilityItems  [{ key, label, isAvailable, icon }]
 * @param pendingLabel  Text, solange noch kein Wert berechnet ist
 */
const WaitTimeChips = ({
  waitTimeItems,
  specialCapabilityItems = [],
  compact = false,
  chipHeight = 26,
  pendingLabel = '–',
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: compact ? 0.5 : 1,
      flex: 1,
      minWidth: 0,
      // Mobil eine wischbare Zeile statt umbrechender Chips
      flexWrap: compact ? 'nowrap' : 'wrap',
      overflowX: compact ? 'auto' : 'visible',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
      '& > *': { flexShrink: 0 },
    }}
  >
    {waitTimeItems.map((item) => {
      const severity = getWaitTimeSeverity(item.value);
      const fullLabel = `${item.label} ${item.value || pendingLabel}`;
      const chip = (
        <Chip
          key={item.key}
          icon={item.icon}
          label={compact
            // Längere Platzhalter („Berechnung…") passen nicht in die Kurzform
            ? `${item.shortLabel ? `${item.shortLabel} ` : ''}${shortWaitTime(item.value, pendingLabel.length > 2 ? '…' : pendingLabel)}`
            : fullLabel}
          aria-label={fullLabel}
          size="small"
          variant="outlined"
          sx={chipSx(severity, compact, chipHeight)}
        />
      );
      return compact
        ? <Tooltip key={item.key} title={fullLabel} enterTouchDelay={0}>{chip}</Tooltip>
        : chip;
    })}

    {specialCapabilityItems.length > 0 && (
      <MedicalIcon
        fontSize="small"
        sx={{ color: 'error.main', ml: compact ? 0 : 0.5 }}
        titleAccess="Krankenfahrt-Spezialfahrzeuge"
      />
    )}
    {specialCapabilityItems.map((item) => {
      const severity = item.isAvailable ? 'success' : 'error';
      const fullLabel = `${item.label} ${item.isAvailable ? 'Verfügbar' : 'Nicht verfügbar'}`;
      // Mobil nur das Symbol in Ampelfarbe (grün = verfügbar, rot = nicht)
      if (compact) {
        return (
          <Tooltip key={item.key} title={fullLabel} enterTouchDelay={0}>
            <Box
              component="span"
              role="img"
              aria-label={fullLabel}
              sx={{
                display: 'inline-flex',
                color: readableSeverityColor(severity),
                '& .MuiSvgIcon-root': { fontSize: 20 },
              }}
            >
              {item.icon}
            </Box>
          </Tooltip>
        );
      }
      return (
        <Chip
          key={item.key}
          icon={item.icon}
          label={fullLabel}
          aria-label={fullLabel}
          size="small"
          variant="outlined"
          sx={chipSx(severity, compact, chipHeight)}
        />
      );
    })}
  </Box>
);

export default WaitTimeChips;
